package com.example.readingnotes.data.remote.api

import com.example.readingnotes.data.remote.dto.*
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody

expect fun createHttpClient(baseUrl: String): HttpClient

class ApiClient(
    private val baseUrl: String,
) {
    private val client = createHttpClient(baseUrl)

    val httpClient = client

    // Auth
    suspend fun login(password: String): LoginResponseDto =
        client
            .post(ApiRoutes.AUTH_LOGIN) {
                setBody(LoginRequestDto(password))
            }.body()

    suspend fun logout(): LogoutResponseDto = client.post(ApiRoutes.AUTH_LOGOUT).body()

    suspend fun getAuthState(): AuthStateResponseDto = client.get(ApiRoutes.AUTH_ME).body()

    // Posts
    suspend fun getPosts(page: Int = 1, pageSize: Int = 20): List<PostDto> =
        client
            .get(ApiRoutes.POSTS) {
                parameter("page", page)
                parameter("pageSize", pageSize)
            }.body()

    suspend fun getPost(id: String): PostDto = client.get(ApiRoutes.post(id)).body()

    suspend fun createPost(request: CreatePostRequestDto): PostDto =
        client
            .post(ApiRoutes.POSTS) {
                setBody(request)
            }.body()

    // Comments
    suspend fun getComments(postId: String): List<CommentDto> = client.get(ApiRoutes.comments(postId)).body()

    suspend fun createComment(postId: String, request: CreateCommentRequestDto): CommentDto =
        client
            .post(ApiRoutes.comments(postId)) {
                setBody(request)
            }.body()

    // New Auth methods
    suspend fun register(request: RegisterRequestDto): UserDto =
        client
            .post(ApiRoutes.AUTH_REGISTER) {
                setBody(request)
            }.body()

    suspend fun loginWithUsername(request: UsernameLoginRequestDto): UsernameLoginResponseDto =
        client
            .post(ApiRoutes.AUTH_LOGIN_USERNAME) {
                setBody(request)
            }.body()

    suspend fun verifyTotp(request: TotpVerifyRequestDto): TotpVerifyResponseDto =
        client
            .post(ApiRoutes.AUTH_VERIFY_TOTP) {
                setBody(request)
            }.body()

    suspend fun setupTotp(): TotpSetupResponseDto = client.post(ApiRoutes.AUTH_SETUP_TOTP).body()

    suspend fun updateUsername(request: UpdateUsernameRequestDto): UserDto =
        client
            .put(ApiRoutes.AUTH_USERNAME) {
                setBody(request)
            }.body()

    suspend fun checkUsernameAvailable(username: String): UsernameAvailableResponseDto =
        client
            .get(
                ApiRoutes.checkUsername(username),
            ).body()

    // Email authentication methods
    suspend fun sendVerificationCode(request: SendVerificationCodeRequestDto): SendVerificationCodeResponseDto =
        client
            .post(ApiRoutes.AUTH_SEND_VERIFICATION_CODE) {
                setBody(request)
            }.body()

    suspend fun verifyEmail(request: VerifyEmailRequestDto): EmailAuthResponseDto =
        client
            .post(ApiRoutes.AUTH_VERIFY_EMAIL) {
                setBody(request)
            }.body()

    suspend fun loginWithCode(request: LoginWithCodeRequestDto): EmailAuthResponseDto =
        client
            .post(ApiRoutes.AUTH_LOGIN_WITH_CODE) {
                setBody(request)
            }.body()

    // ============================================================================
    // OIDC authentication methods
    // ============================================================================

    /**
     * Get OIDC authorization URL
     * Initiates OIDC login flow by returning authorization URL and state
     */
    suspend fun oidcLogin(): OidcLoginResponseDto = client.get(ApiRoutes.OIDC_LOGIN).body()

    /**
     * Handle OIDC callback
     * Exchanges authorization code for tokens and returns user data
     */
    suspend fun oidcCallback(code: String, state: String): OidcCallbackResponseDto =
        client
            .get(ApiRoutes.OIDC_CALLBACK) {
                parameter("code", code)
                parameter("state", state)
            }.body()

    /**
     * Refresh OIDC access token
     */
    suspend fun oidcRefresh(): OidcRefreshResponseDto = client.post(ApiRoutes.OIDC_REFRESH).body()

    /**
     * Logout from OIDC session
     */
    suspend fun oidcLogout(): OidcLogoutResponseDto = client.post(ApiRoutes.OIDC_LOGOUT).body()

    /**
     * Get OIDC configuration
     */
    suspend fun oidcConfig(): OidcConfigResponseDto = client.get(ApiRoutes.OIDC_CONFIG).body()

    // ============================================================================
    // Profile management methods
    // ============================================================================

    /**
     * Get user profile
     */
    suspend fun getProfile(): ProfileResponseDto = client.get(ApiRoutes.USERS_PROFILE).body()

    /**
     * Update user profile
     */
    suspend fun updateProfile(request: UpdateProfileRequestDto): ProfileResponseDto =
        client
            .put(ApiRoutes.USERS_PROFILE) {
                setBody(request)
            }.body()

    // New Post methods
    suspend fun updatePost(id: String, request: UpdatePostRequestDto, totpCode: String? = null): PostDto =
        client
            .put(ApiRoutes.post(id)) {
                setBody(request)
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    suspend fun deletePost(id: String, totpCode: String? = null): DeleteResponseDto =
        client
            .delete(ApiRoutes.post(id)) {
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    suspend fun updatePostStatus(id: String, status: String, totpCode: String? = null): PostDto =
        client
            .patch(ApiRoutes.postStatus(id)) {
                setBody(UpdatePostStatusRequestDto(status))
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    // New Comment methods
    suspend fun deleteComment(id: String, totpCode: String? = null): DeleteResponseDto =
        client
            .delete(ApiRoutes.comment(id)) {
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    suspend fun bulkDeleteComments(ids: List<String>, totpCode: String? = null): BulkDeleteResponseDto =
        client
            .post(ApiRoutes.COMMENTS_BULK_DELETE) {
                setBody(BulkDeleteRequestDto(ids))
                totpCode?.let { header("X-TOTP-Code", it) }
            }.body()

    // Audit logs
    suspend fun getAuditLogs(
        page: Int = 1,
        pageSize: Int = 20,
        startDate: String? = null,
        endDate: String? = null,
        actionType: String? = null,
    ): PaginatedAuditLogsResponseDto =
        client
            .get(ApiRoutes.AUDIT_LOGS) {
                parameter("page", page)
                parameter("pageSize", pageSize)
                startDate?.let { parameter("startDate", it) }
                endDate?.let { parameter("endDate", it) }
                actionType?.let { parameter("actionType", it) }
            }.body()

    // Images
    suspend fun getImageUploadUrl(request: ImageUploadUrlRequestDto): ImageUploadUrlResponseDto =
        client
            .post(ApiRoutes.IMAGES_UPLOAD_URL) {
                setBody(request)
            }.body()

    suspend fun confirmImageUpload(request: ImageConfirmRequestDto): ImageDto =
        client
            .post(ApiRoutes.IMAGES_CONFIRM) {
                setBody(request)
            }.body()

    suspend fun getImage(id: String): ImageDto = client.get(ApiRoutes.image(id)).body()

    suspend fun deleteImage(id: String): DeleteResponseDto = client.delete(ApiRoutes.image(id)).body()

    // Tags
    suspend fun getTags(): List<TagDto> = client.get(ApiRoutes.TAGS).body()

    suspend fun getTag(id: String): TagDto = client.get(ApiRoutes.tag(id)).body()

    suspend fun getPopularTags(limit: Int = 10): List<TagWithCountDto> =
        client
            .get(ApiRoutes.TAGS_POPULAR) {
                parameter("limit", limit)
            }.body()

    suspend fun getTagsForPost(postId: String): List<TagDto> = client.get(ApiRoutes.tagsForPost(postId)).body()

    suspend fun createTag(request: CreateTagRequest): TagDto =
        client
            .post(ApiRoutes.TAGS) {
                setBody(request)
            }.body()

    suspend fun updateTag(id: String, request: UpdateTagRequest): TagDto =
        client
            .put(ApiRoutes.tag(id)) {
                setBody(request)
            }.body()

    suspend fun deleteTag(id: String): DeleteResponseDto = client.delete(ApiRoutes.tag(id)).body()

    suspend fun addTagToPost(postId: String, tagId: String): Unit =
        client.post(ApiRoutes.addTagToPost(postId, tagId)).body()

    suspend fun removeTagFromPost(postId: String, tagId: String): Unit =
        client.delete(ApiRoutes.addTagToPost(postId, tagId)).body()

    suspend fun searchTags(query: String): List<TagDto> =
        client
            .get(ApiRoutes.TAGS_SEARCH) {
                parameter("q", query)
            }.body()

    // Search
    suspend fun searchPosts(
        query: String,
        page: Int = 1,
        pageSize: Int = 20,
        status: String? = null,
    ): SearchResponseDto =
        client
            .get(ApiRoutes.SEARCH) {
                parameter("q", query)
                parameter("page", page)
                parameter("pageSize", pageSize)
                status?.let { parameter("status", it) }
            }.body()

    suspend fun getSearchSuggestions(
        query: String,
        limit: Int = 5,
    ): SearchSuggestionsResponseDto =
        client
            .get(ApiRoutes.SEARCH_SUGGESTIONS) {
                parameter("q", query)
                parameter("limit", limit)
            }.body()

    // ============================================================================
    // Search History API
    // ============================================================================

    /**
     * Get user's search history
     */
    suspend fun getSearchHistory(): SearchHistoryListDto =
        client.get(ApiRoutes.SEARCH_HISTORY).body()

    /**
     * Clear user's search history
     */
    suspend fun clearSearchHistory(): Unit =
        client.delete(ApiRoutes.SEARCH_HISTORY).body()

    // Notifications
    suspend fun getNotificationSettings(): NotificationSettingsDto =
        client.get(ApiRoutes.PUSH_SETTINGS).body()

    suspend fun updateNotificationSettings(request: UpdateNotificationSettingsRequestDto): NotificationSettingsDto =
        client
            .put(ApiRoutes.PUSH_SETTINGS) {
                setBody(request)
            }.body()

    suspend fun getNotifications(
        limit: Int = 30,
        offset: Int = 0,
        unreadOnly: Boolean = false,
    ): NotificationsResponseDto =
        client
            .get(ApiRoutes.PUSH_NOTIFICATIONS) {
                parameter("limit", limit)
                parameter("offset", offset)
                parameter("unread", unreadOnly.toString())
            }.body()

    suspend fun markNotificationAsRead(id: String): MarkAsReadResponseDto =
        client.patch(ApiRoutes.pushNotification(id)).body()

    suspend fun markAllNotificationsAsRead(): MarkAllAsReadResponseDto =
        client.post(ApiRoutes.PUSH_NOTIFICATIONS_READ_ALL).body()

    // ============================================================================
    // Stripe Connect API
    // ============================================================================

    /**
     * Get Stripe Connect onboarding link
     */
    suspend fun getStripeConnectOnboardingLink(
        redirectUrl: String,
        refreshUrl: String? = null,
    ): OnboardingLinkDto =
        client
            .get(ApiRoutes.STRIPE_CONNECT_ONBOARDING_LINK) {
                parameter("redirect_url", redirectUrl)
                refreshUrl?.let { parameter("refresh_url", it) }
            }.body()

    /**
     * Get Stripe Connect account status
     */
    suspend fun getStripeConnectStatus(): StripeConnectStatusDto =
        client.get(ApiRoutes.STRIPE_CONNECT_STATUS).body()

    /**
     * Handle Stripe Connect OAuth callback
     */
    suspend fun handleStripeConnectCallback(code: String): StripeConnectCallbackResponseDto =
        client
            .post(ApiRoutes.STRIPE_CONNECT_CALLBACK) {
                setBody(StripeConnectCallbackDto(code))
            }.body()

    /**
     * Refresh Stripe Connect account status
     */
    suspend fun refreshStripeConnectStatus(): StripeConnectStatusDto =
        client.post(ApiRoutes.STRIPE_CONNECT_REFRESH).body()

    // ============================================================================
    // Paid Content API
    // ============================================================================

    /**
     * Set price for a post
     */
    suspend fun setPostPrice(
        tenantSlug: String,
        postId: String,
        request: SetPriceRequestDto,
    ): PurchasableContentDto =
        client
            .post(ApiRoutes.setPostPrice(tenantSlug, postId)) {
                setBody(request)
            }.body()

    /**
     * Create checkout session for purchasing content
     */
    suspend fun createPurchaseSession(
        contentId: String,
        request: CreatePurchaseRequestDto,
    ): PurchaseSessionDto =
        client
            .post(ApiRoutes.purchaseCheckout(contentId)) {
                setBody(request)
            }.body()

    /**
     * Check purchase status for content
     */
    suspend fun getPurchaseStatus(contentId: String): ContentAccessResultDto =
        client.get(ApiRoutes.purchaseStatus(contentId)).body()

    /**
     * Get user's purchases
     */
    suspend fun getUserPurchases(): PurchasesListDto =
        client.get(ApiRoutes.PURCHASES).body()

    /**
     * Get sales dashboard for creator
     */
    suspend fun getSalesDashboard(tenantSlug: String): SalesDashboardDto =
        client.get(ApiRoutes.salesDashboard(tenantSlug)).body()

    /**
     * Refund a purchase
     */
    suspend fun refundPurchase(purchaseId: String): ContentPurchaseDto =
        client.post(ApiRoutes.purchaseRefund(purchaseId)).body()
}
